/**
 * Traduction des erreurs de l'API en quelque chose d'utilisable par l'interface.
 *
 * L'API renvoie du RFC 7807 (`application/problem+json`) avec une extension `code`
 * stable — c'est ce `code`, et non le message, qui doit piloter le comportement du
 * front : le message peut evoluer sans preavis, le code non.
 */

/** Forme des reponses d'erreur de l'API (RFC 7807 + extensions Astrolabe). */
export interface ProblemeApi {
  status?: number;
  title?: string;
  detail?: string;
  instance?: string;
  /** Code metier stable, ex. `sales.facture.immuable`. */
  code?: string;
  /** Identifiant de correlation, a citer au support. */
  correlationId?: string;
  /** Erreurs par champ, renvoyees par la validation. */
  erreurs?: Record<string, string[]>;
  errors?: Record<string, string[]>;
}

/** Nature de l'erreur, deduite du code HTTP. Miroir de l'`ErrorType` du back. */
export type NatureErreur =
  | 'validation'
  | 'introuvable'
  | 'conflit'
  | 'interdit'
  | 'non-authentifie'
  | 'reseau'
  | 'serveur';

/**
 * Erreur d'API normalisee.
 *
 * On etend `Error` pour rester compatible avec TanStack Query, les frontieres
 * d'erreur React et les outils de supervision, tout en portant les champs dont
 * l'interface a besoin.
 */
export class ErreurApi extends Error {
  readonly nature: NatureErreur;
  readonly code: string;
  readonly statut: number;
  readonly correlationId: string | undefined;
  /** Erreurs par champ, prêtes a etre injectees dans React Hook Form. */
  readonly champs: Record<string, string[]>;

  constructor(options: {
    message: string;
    nature: NatureErreur;
    code: string;
    statut: number;
    correlationId?: string | undefined;
    champs?: Record<string, string[]>;
  }) {
    super(options.message);
    this.name = 'ErreurApi';
    this.nature = options.nature;
    this.code = options.code;
    this.statut = options.statut;
    this.correlationId = options.correlationId;
    this.champs = options.champs ?? {};
  }

  /** Vrai si reessayer a une chance d'aboutir. Une erreur metier, elle, ne s'ameliore pas. */
  get estTransitoire(): boolean {
    return this.nature === 'reseau' || this.nature === 'serveur';
  }
}

const NATURES: Record<number, NatureErreur> = {
  400: 'validation',
  401: 'non-authentifie',
  403: 'interdit',
  404: 'introuvable',
  409: 'conflit',
  422: 'validation',
};

/** Messages de repli, quand l'API n'en fournit pas d'exploitable. */
const MESSAGES_PAR_DEFAUT: Record<NatureErreur, string> = {
  validation: 'Certaines informations saisies ne sont pas valides.',
  introuvable: "Cet element n'existe pas ou n'est plus accessible.",
  conflit: "Cette action n'est pas possible dans l'etat actuel.",
  interdit: "Vous n'avez pas les droits necessaires pour cette action.",
  'non-authentifie': 'Votre session a expire. Reconnectez-vous.',
  reseau: 'Impossible de joindre le serveur. Verifiez votre connexion.',
  serveur: 'Une erreur inattendue est survenue de notre cote.',
};

export function natureDepuisStatut(statut: number): NatureErreur {
  return NATURES[statut] ?? (statut >= 500 ? 'serveur' : 'serveur');
}

/** Construit une `ErreurApi` a partir d'une reponse HTTP en echec. */
export function depuisReponse(statut: number, corps: unknown): ErreurApi {
  const probleme = (corps ?? {}) as ProblemeApi;
  const nature = natureDepuisStatut(statut);

  const champs = probleme.erreurs ?? probleme.errors ?? {};

  return new ErreurApi({
    message: probleme.detail?.trim() || probleme.title?.trim() || MESSAGES_PAR_DEFAUT[nature],
    nature,
    code: probleme.code ?? `http.${statut}`,
    statut,
    correlationId: probleme.correlationId,
    champs,
  });
}

/** Erreur de transport : le serveur n'a pas repondu du tout. */
export function erreurReseau(cause: unknown): ErreurApi {
  return new ErreurApi({
    message: MESSAGES_PAR_DEFAUT.reseau,
    nature: 'reseau',
    code: 'reseau.injoignable',
    statut: 0,
    correlationId: cause instanceof Error ? cause.message : undefined,
  });
}

/** Message pret a afficher, quelle que soit la nature de ce qui a ete attrape. */
export function messageLisible(erreur: unknown): string {
  if (erreur instanceof ErreurApi) return erreur.message;
  if (erreur instanceof Error) return erreur.message;
  return MESSAGES_PAR_DEFAUT.serveur;
}
